import React, { useState } from 'react';
import { FadeIn, CTAButton, Section, SectionTitle, Accent, pad2 } from './Shared';
import { LocalCurrencyNote } from './LocalCurrencyNote';
import { useCountdown } from '../hooks/useCountdown';
import { markCheckoutStarted } from '../hooks/useExitIntent';
import { PRODUCTS } from './MetaPixel';
import { volumes, sessionFeatures, featureChips } from '../data/volumes';
import { bonuses } from '../data/bonuses';
import { Check, AlertTriangle, Plus, PrinterIcon, ArrowRightIcon, Lock, ZapIcon, ShieldCheck, BookOpenIcon, GiftIcon } from 'lucide-react';
export const ProductTomos = () => {
  const [openVolume, setOpenVolume] = useState<number | null>(0);
  return <Section className="bg-slate-950" data-id="element-28" data-mp="2cpykdjm5u63c">
      <FadeIn data-id="element-29" data-mp="2cox0cpn6299h">
        <SectionTitle sub="Un volume ogni due mesi. Lo apri, scegli la sessione, insegni." data-id="element-30" data-mp="2cof43t0i7v1k">
          6 <Accent data-id="element-31" data-mp="30ercf4znvtso">volumi</Accent> completi
        </SectionTitle>
      </FadeIn>

      {/* Year timeline */}
      <FadeIn delay={0.05} className="mx-auto mb-8 max-w-4xl" data-id="element-32" data-mp="357u4t9spcx7a">
        <div className="grid grid-cols-6 gap-1.5 md:gap-2" aria-label="Il tuo anno, volume per volume" data-id="element-33" data-mp="2hktiqoasyglq">
          {volumes.map(v => <div key={v.number} data-id="element-34" data-mp="2cnt963equvpv">
              <div className={`h-1.5 rounded-full ${v.bar}`} data-id="element-35" data-mp="2cknl8rhupzvq" />
              <p className={`mt-2 font-display text-[11px] font-extrabold uppercase md:text-sm ${v.text}`} data-id="element-36" data-mp="2rc691bgycypn">
                Vol. {v.number}
              </p>
              <p className="hidden text-xs text-slate-500 md:block" data-id="element-37" data-mp="30bt299zj2y7m">{v.months}</p>
            </div>)}
        </div>
      </FadeIn>

      <FadeIn delay={0.1} className="mb-12 md:mb-16" data-id="element-38" data-mp="2w9fwilks70au">
        <ul className="mx-auto flex max-w-3xl flex-wrap justify-center gap-1.5 md:gap-2" data-id="element-39" data-mp="2wcu2e3snlrn1">
          {featureChips.map(chip => <li key={chip} className="inline-flex items-center gap-1 whitespace-nowrap rounded-full border border-white/10 bg-white/5 px-2.5 py-1 text-[11px] font-medium text-slate-200 md:text-sm" data-id="element-40" data-mp="35bfg9ln6kihh">
              <Check size={12} className="text-sky-400" strokeWidth={3} aria-hidden="true" data-id="element-41" data-mp="1axeh0znpxtgv" />
              {chip}
            </li>)}
        </ul>
        <p className="mt-4 flex items-center justify-center gap-1.5 text-center text-xs text-slate-400 md:text-sm" data-id="element-42" data-mp="3b0pbtjhh1wuq">
          <PrinterIcon size={14} aria-hidden="true" data-id="element-43" data-mp="3fyqx8b2de89d" />
          <span data-id="element-44" data-mp="3jzsyexfukudz"><span className="font-semibold text-slate-200" data-id="element-45" data-mp="3jzsw2qsdkiat">Pronto da stampare</span> o da usare direttamente da smartphone o tablet.</span>
        </p>
      </FadeIn>

      <h3 className="mb-5 text-center text-xs font-semibold uppercase tracking-widest text-slate-400" data-id="element-46" data-mp="3ty9to6ltjv5u">
        Cosa ricevi
      </h3>
      <div className="mb-16 grid grid-cols-1 items-start gap-3 md:mb-24 md:grid-cols-2 md:gap-4 lg:grid-cols-3" data-id="element-47" data-mp="2r5nfdxi0cyh">
        {volumes.map((v, i) => {
        const isOpen = openVolume === i;
        return <FadeIn key={v.number} delay={Math.min(i * 0.04, 0.2)} data-id="element-48" data-mp="bkioj35pukvn" data-mpi={i}>
              <button type="button" onClick={() => setOpenVolume(isOpen ? null : i)} className={`group w-full rounded-2xl border border-t-[3px] bg-white/[0.03] p-5 text-left transition-[border-color,background-color] duration-200 ease-out hover:bg-white/[0.05] focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-950 md:p-6 ${v.border} ${isOpen ? 'border-x-white/20 border-b-white/20' : 'border-x-white/10 border-b-white/10'}`} aria-expanded={isOpen} data-id="element-49" data-mp="gjdj12d1pxvl">
                <div className="flex items-start justify-between gap-3" data-id="element-50" data-mp="hndw6ohlwbvl">
                  <div className="min-w-0" data-id="element-51" data-mp="1ecv7bizcyq4o">
                    <p className={`font-display text-xs font-extrabold uppercase tracking-wide ${v.text}`} data-id="element-52" data-mp="1ebjvcvvrec2h">
                      Volume {v.number} · {v.months}
                    </p>
                    <h4 className="mt-1.5 font-display text-lg font-extrabold leading-tight text-white md:text-xl" data-id="element-53" data-mp="14ma5lmoe15hj">
                      {v.title}
                    </h4>
                    <p className="mt-1 text-sm text-slate-400" data-id="element-54" data-mp="1x34b2xasmpyh">{v.tagline}</p>
                  </div>
                  <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white/5 text-white transition-transform duration-200 ease-out ${isOpen ? 'rotate-45' : ''}`} aria-hidden="true" data-id="element-55" data-mp="1scw8kzjjbzko">
                    <Plus size={16} data-id="element-56" data-mp="1htzi2r5uhltw" />
                  </span>
                </div>
                <div className={`grid transition-[grid-template-rows,opacity] duration-200 ease-out ${isOpen ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'}`} data-id="element-57" data-mp="13u7y89sndb7z">
                  <div className="overflow-hidden" data-id="element-58" data-mp="17xcys6t632tz">
                    <p className="pt-4 text-[15px] leading-relaxed text-slate-300" data-id="element-59" data-mp="1vm5npjbdgaf3">
                      {v.desc}
                    </p>
                    <p className="mt-3 inline-flex rounded-md bg-white/5 px-2 py-0.5 text-xs font-semibold text-slate-200" data-id="element-60" data-mp="20mbg51pgu0y8">
                      {v.sessions}
                    </p>
                  </div>
                </div>
              </button>
            </FadeIn>;
      })}
      </div>

      <FadeIn data-id="element-61" data-mp="2eip1ilx4emhz">
        <div className="grid gap-8 rounded-3xl border border-white/10 bg-slate-900/60 p-6 md:grid-cols-[0.9fr_1.1fr] md:gap-12 md:p-12" data-id="element-62" data-mp="2ei5i10rtnq1u">
          <div className="flex flex-col" data-id="element-63" data-mp="7uy4jrjsmsr6">
            <h3 className="font-display text-3xl font-black uppercase italic leading-[0.95] tracking-tight text-white md:text-5xl" data-id="element-64" data-mp="7twu9palqaai">
              Ogni sessione <Accent data-id="element-65" data-mp="hm2gsji33zib">include</Accent>
            </h3>
            
          </div>
          <ul className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2" data-id="element-66" data-mp="cmg368ycgh8z">
            {sessionFeatures.map(feature => <li key={feature.title} className="flex items-start gap-3" data-id="element-67" data-mp="3l1zs4vrzfdi8">
                <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-emerald-500/15" data-id="element-68" data-mp="3l42tud2hyv2s">
                  <Check size={12} className="text-emerald-400" strokeWidth={3} aria-hidden="true" data-id="element-69" data-mp="3v05xxogwt3sd" />
                </span>
                <div data-id="element-70" data-mp="3q0b4pe9nw245">
                  <p className="font-semibold leading-snug text-white" data-id="element-71" data-mp="3pzrn5ynxbfqn">{feature.title}</p>
                  <p className="text-sm leading-normal text-slate-400" data-id="element-72" data-mp="1a8vk99muzcw0">{feature.desc}</p>
                </div>
              </li>)}
          </ul>
        <p className="mt-5 text-lg font-semibold leading-snug text-sky-300 md:mt-auto md:text-xl" data-id="element-73" data-mp="15dg8cn8evr28">
              Apri il volume, scegli la sessione e insegna. Così semplice. 365
              lezioni ad alte prestazioni. Pronte oggi.
            </p></div>
      </FadeIn>
    </Section>;
};
export const Bonuses = () => {
  return <Section className="bg-slate-900/40" data-id="element-74" data-mp="1rc463zz88bck">
      <FadeIn data-id="element-75" data-mp="1rb2jcy8um0f0">
        <SectionTitle sub="Materiali esclusivi, senza alcun costo aggiuntivo" data-id="element-76" data-mp="1r9hyy1g8zxuv">
          + 5 <Accent data-id="element-77" data-mp="tpdkrcp0e0nr">bonus</Accent> strategici
        </SectionTitle>
      </FadeIn>

      <div className="mb-8 grid grid-cols-1 gap-3 sm:grid-cols-2 md:mb-10 md:gap-4 lg:grid-cols-5" data-id="element-78" data-mp="ynyhv38ys47z">
        {bonuses.map((bono, i) => <FadeIn key={bono.num} delay={Math.min(i * 0.04, 0.2)} className={i === bonuses.length - 1 ? 'sm:col-span-2 lg:col-span-1' : ''} data-id="element-79" data-mp="13kp0nyc5ldx3" data-mpi={i}>
            <article className="flex h-full gap-4 overflow-hidden rounded-2xl border border-white/10 bg-white/[0.03] p-3 sm:flex-col sm:gap-0 sm:p-0" data-id="element-80" data-mp="axktbmpl8ni5">
              <img src={bono.image} alt={`Copertina Bonus ${bono.num}: ${bono.title}`} loading="lazy" decoding="async" width={1024} height={1024} className="aspect-square w-28 shrink-0 self-start rounded-xl object-cover sm:w-full sm:rounded-none" data-id="element-81" data-mp="fukx1qyyte63" />
              <div className="flex min-w-0 flex-1 flex-col sm:p-4" data-id="element-82" data-mp="2k5hfuw46yiy2">
                <p className="font-display text-xs font-extrabold uppercase tracking-wide text-sky-400" data-id="element-83" data-mp="2k7tvv3qe2j8h">
                  Bonus {bono.num}
                </p>
                <h3 className="mt-1 font-display text-base font-extrabold leading-tight text-white" data-id="element-84" data-mp="2a8mjojve4umy">
                  {bono.title}
                </h3>
                <p className="mt-1.5 text-sm leading-snug text-slate-400" data-id="element-85" data-mp="21fiz7rmbvhit">
                  {bono.desc}
                </p>
                <div className="mt-auto flex items-center justify-between gap-2 pt-3" data-id="element-86" data-mp="1wh7ntcgdwyyh">
                  <span className="text-sm text-slate-500 line-through" data-id="element-87" data-mp="1rhv0yrpijrvw">
                    {bono.value}
                  </span>
                  <span className="rounded-full bg-emerald-500/15 px-2.5 py-0.5 text-xs font-bold uppercase tracking-wide text-emerald-400" data-id="element-88" data-mp="1mjjrwj71lleq">
                    Gratis
                  </span>
                </div>
              </div>
            </article>
          </FadeIn>)}
      </div>

      <FadeIn className="mx-auto flex max-w-3xl flex-col items-center justify-between gap-4 rounded-2xl border border-emerald-500/25 bg-emerald-500/[0.06] px-6 py-5 text-center md:flex-row md:text-left" data-id="element-89" data-mp="31f1nenczkgfr">
        <div className="flex items-center gap-3" data-id="element-90" data-mp="2rndxiqw2v7ge">
          <span className="hidden h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-emerald-500/15 md:flex" data-id="element-91" data-mp="2rmm1jeykj7pl">
            <GiftIcon size={22} className="text-emerald-400" aria-hidden="true" data-id="element-92" data-mp="2io0kjo8qw0qx" />
          </span>
          <div data-id="element-93" data-mp="2nmln3ubgywij">
            <p className="text-sm text-slate-300" data-id="element-94" data-mp="2nm27wld7em87">Valore totale dei 5 bonus: <span className="text-slate-500 line-through" data-id="element-95" data-mp="28pkvdqta31k2">50€</span></p>
            <p className="text-xs text-slate-500" data-id="element-96" data-mp="28qcqz1mvkzac">Inclusi in Megapack Funzionale 365, senza costi aggiuntivi.</p>
          </div>
        </div>
        <p className="font-display text-3xl font-black uppercase italic leading-none tracking-tight text-emerald-400 md:text-4xl" data-id="element-97" data-mp="1u2m53z65qd6x">
          Gratis oggi
        </p>
      </FadeIn>
    </Section>;
};
export const UrgencyCounter = () => {
  const {
    hours,
    minutes,
    seconds
  } = useCountdown();
  const units = [{
    value: hours,
    label: 'ore'
  }, {
    value: minutes,
    label: 'min'
  }, {
    value: seconds,
    label: 'sec'
  }];
  return <Section className="bg-slate-950 !py-14 md:!py-20" data-id="element-98" data-mp="2czx55lzm5anf">
      <FadeIn className="mx-auto max-w-xl text-center" data-id="element-99" data-mp="290o92ah3p0op">
        <p className="text-xs font-semibold uppercase tracking-widest text-sky-400" data-id="element-100" data-mp="28y24mjpib1ap">
          Prezzo di lancio — scade tra
        </p>
        <div className="mt-4 flex items-start justify-center gap-2 md:gap-3" role="timer" aria-live="off" data-id="element-101" data-mp="2hryeaz1hil98">
          {units.map((unit, i) => <React.Fragment key={unit.label} data-id="element-102" data-mp="2wqiomzst13cn" data-mpi={i}>
              {i > 0 && <span className="pt-3 font-display text-4xl font-black text-slate-600 md:pt-4 md:text-5xl" aria-hidden="true" data-id="element-103" data-mp="2rqwbeqcz7n3u">:</span>}
              <div className="flex w-20 flex-col items-center rounded-2xl border border-white/10 bg-white/[0.04] py-3 md:w-24 md:py-4" data-id="element-104" data-mp="2qykbdeibz7c2">
                <span className="font-display text-4xl font-black tabular-nums leading-none tracking-tight text-white md:text-5xl" data-id="element-105" data-mp="2m4pmzxrs646n">
                  {pad2(unit.value)}
                </span>
                <span className="mt-1.5 text-[11px] font-semibold uppercase tracking-widest text-slate-500" data-id="element-106" data-mp="2d4170xitttr4">
                  {unit.label}
                </span>
              </div>
            </React.Fragment>)}
        </div>

        <p className="mt-6 flex items-center justify-center gap-2 text-base font-semibold text-amber-300 md:text-lg" data-id="element-107" data-mp="30vopcr20vwhs">
          <AlertTriangle size={18} aria-hidden="true" data-id="element-108" data-mp="2vx55qxawiwsl" />
          Dopo, il prezzo sale. Per sempre.
        </p>

        <div className="mt-6 rounded-2xl border border-white/10 bg-white/[0.03] p-4 text-left" data-id="element-109" data-mp="1pgtbh7b79z2a">
          <div className="mb-3 flex justify-between gap-3 text-sm text-slate-300" data-id="element-110" data-mp="1kh6z10374muj">
            <span className="flex items-center gap-2" data-id="element-111" data-mp="3w5bw8qbvum8t">
              <span className="h-2 w-2 rounded-full bg-sky-400 animate-pulse" aria-hidden="true" data-id="element-112" data-mp="18v3cw2qt27" />
              Posti a prezzo di lancio
            </span>
            <span className="font-semibold text-sky-400" data-id="element-113" data-mp="8zuax0a5vtza">Ne restano pochi</span>
          </div>
          <div className="mb-2 h-2 w-full overflow-hidden rounded-full bg-white/10" role="progressbar" aria-valuenow={71} aria-valuemin={0} aria-valuemax={100} aria-label="Posti già occupati" data-id="element-114" data-mp="dr2n242vltg5">
            <div className="h-full rounded-full bg-blue-500" style={{
            width: '71%'
          }} data-id="element-115" data-mp="noh0rvxf044r" />
          </div>
          <p className="text-right text-xs text-slate-500" data-id="element-116" data-mp="spekicy2p68d">
            71% dei posti già occupati
          </p>
        </div>
      </FadeIn>
    </Section>;
};
export const OfferCard = ({
  id,
  footnote
}: {
  id?: string;
  footnote: string;
}) => {
  const includes = [{
    icon: BookOpenIcon,
    label: '6 Volumi Completi',
    detail: '365 sessioni pronte da insegnare',
    price: '69€'
  }, {
    icon: GiftIcon,
    label: '5 Bonus Esclusivi',
    detail: 'Adattamenti, riscaldamenti, chiusure e frasi',
    price: '50€'
  }];
  return <div id={id} className="relative rounded-3xl border border-sky-400/40 bg-slate-900 p-5 shadow-2xl shadow-blue-950/50 md:p-8" data-id="element-117" data-mp="pt9cjrhpc2g">
      <div className="absolute -top-3 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full bg-blue-600 px-3.5 py-1 text-[11px] font-bold uppercase tracking-widest text-white" data-id="element-118" data-mp="al4pn6ijqrcb">
        Il più scelto
      </div>

      <p className="mt-2 text-center font-display text-xl font-black uppercase italic tracking-tight text-white md:text-2xl" data-id="element-119" data-mp="jlb0slqrvf8b">
        Megapack Funzionale <Accent data-id="element-120" data-mp="ydbkg18miq9m">365™</Accent>
      </p>

      <ul className="mt-5 divide-y divide-white/10 border-y border-white/10" data-id="element-121" data-mp="tbm7uh5r64jf">
        {includes.map(({
        icon: Icon,
        label,
        detail,
        price
      }) => <li key={label} className="flex items-center justify-between gap-3 py-3" data-id="element-122" data-mp="12cu3ergvwr9t">
            <div className="flex min-w-0 items-start gap-3" data-id="element-123" data-mp="12ar5ll9hptuj">
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-white/5" data-id="element-124" data-mp="1lzxaka8tscdp">
                <Icon size={16} className="text-sky-400" aria-hidden="true" data-id="element-125" data-mp="1qwdues8zs4kz" />
              </span>
              <div className="min-w-0" data-id="element-126" data-mp="1cx5xy747rakh">
                <p className="text-sm font-semibold leading-snug text-white md:text-base" data-id="element-127" data-mp="1gzizskfuaobz">{label}</p>
                <p className="text-xs leading-snug text-slate-400 md:text-sm" data-id="element-128" data-mp="1384fmaa74h3c">{detail}</p>
              </div>
            </div>
            <span className="shrink-0 text-sm text-slate-500 line-through" data-id="element-129" data-mp="17zcmozo1zqdc">{price}</span>
          </li>)}
      </ul>

      <div className="mt-6 text-center" data-id="element-130" data-mp="tazpy0h563hs">
        <p className="text-sm text-slate-400" data-id="element-131" data-mp="t7vi8t4gwpmd">
          Valore originale <span className="text-slate-500 line-through" data-id="element-132" data-mp="y9t660ntc8ln">119€</span> · Accesso completo oggi a
        </p>
        <p className="mt-2 font-display font-black italic leading-none tracking-tight text-white" data-id="element-133" data-mp="kal7d8vkb2hz">
          <span className="text-7xl md:text-8xl" data-id="element-134" data-mp="p71v41yuneuj">19</span>
          <span className="align-top text-3xl md:text-4xl" data-id="element-135" data-mp="h7du6k5pafrg">€</span>
          <span className="ml-1 text-base font-bold not-italic text-slate-400" data-id="element-136" data-mp="h9ro2pon9lxf">EUR</span>
        </p>
        <p className="mt-2 text-xs text-slate-400 md:text-sm" data-id="element-137" data-mp="cangg3hdbw97">{footnote}</p>
      </div>

      <div className="mt-6" data-id="element-138" data-mp="7g17e6yrefsa">
        <CTAButton href={PRODUCTS.mf365.checkoutUrl} fullWidth trackingProduct="mf365" onClick={markCheckoutStarted} data-id="element-139" data-mp="3g8slcirvdeh">
          Lo voglio adesso
          <ArrowRightIcon size={18} className="transition-transform duration-200 group-hover/cta:translate-x-0.5" aria-hidden="true" data-id="element-140" data-mp="3uncwhyjmejio" />
        </CTAButton>
        <LocalCurrencyNote className="mt-4" data-id="element-141" data-mp="3kodvn253js68" />
        <div className="mt-3 flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-xs text-slate-500" data-id="element-142" data-mp="3kp5k7t0a0pn0">
          <span className="flex items-center gap-1" data-id="element-143" data-mp="3golmoobsjk0c"><Lock size={12} aria-hidden="true" data-id="element-144" data-mp="1iwr5f2t141at" /> Pagamento sicuro</span>
          <span className="flex items-center gap-1" data-id="element-145" data-mp="1iuwsaao01dha"><ZapIcon size={12} aria-hidden="true" data-id="element-146" data-mp="1euw7876ea419" /> Accesso in pochi minuti</span>
          <span className="flex items-center gap-1" data-id="element-147" data-mp="2wt2b78o5ig4n"><ShieldCheck size={12} aria-hidden="true" data-id="element-148" data-mp="2ws994l050esu" /> Garanzia 7 giorni</span>
        </div>
      </div>
    </div>;
};
export const Pricing = () => {
  return <Section className="bg-slate-900/40" id="pricing" data-id="element-149" data-mp="36iu3fw8s2x5y">
      <div className="mx-auto max-w-lg" data-id="element-150" data-mp="36jdohltq61o7">
        <FadeIn className="mb-10 text-center" data-id="element-151" data-mp="3akfo43rk0fqp">
          <p className="text-sm text-slate-400 md:text-base" data-id="element-152" data-mp="3aj5ovb8yt1ih">
            Sei a un passo dall’avere ogni lezione già risolta…
          </p>
          <h2 className="mx-auto mt-3 font-display text-4xl font-black uppercase italic leading-[0.92] tracking-tight text-white md:text-6xl" data-id="element-153" data-mp="2dwj7iyw49kfh">
            Offerta speciale di <Accent data-id="element-154" data-mp="2mu359xursylc">lancio</Accent>
          </h2>
          <p className="mt-3 text-sm text-slate-400" data-id="element-155" data-mp="2rtxw61ejpo6e">
            Volumi, bonus e 365 sessioni pronte in un unico accesso.
          </p>
        </FadeIn>

        <FadeIn delay={0.05} data-id="element-156" data-mp="244201oo7dp2">
          <OfferCard footnote="Pagamento unico · Nessun abbonamento · Accesso immediato" data-id="element-157" data-mp="26zzjowlw0cz" />
        </FadeIn>
      </div>
    </Section>;
};